import React, { useMemo, useState } from "react";
import Input, { InputTypeEnum } from "src/components/common/Input";
import Button from "src/components/common/Button";
import { ICONS } from "src/assests";
import { ModifierItemOptionsListProps } from "src/interfaces/modifier-items-props.interface";
import FormRowVertical from "src/components/common/FormRowVertical";
import { useDeleteModifierItemOption } from "../hooks/useDeleteModifierItemOption";
import { useAddModifierItemOption } from "../hooks/useAddModifierItemOption";
import { useUpdateModifierItemOption } from "../hooks/useUpdateModifierItemOption";
import { ModifierItemOption } from "src/interfaces/modifier.interface";
import { convertDollarToCents } from "src/helpers";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import {
  buildLocaleTranslationMap,
  buildTranslationsPayload,
  LocaleTranslationMap,
  syncLegacyNameFieldsFromLocaleTranslations,
} from "src/utils/localeTranslations";

const ModifierItemOptionsList = React.memo(
  ({
    modifierItemOptions,
    onRemoveModifierItemOptionFromUI,
    onUpdateModifierItemOption,
    modifierItemCorrelationId,
    hasModifierItemToEdit = false,
  }: ModifierItemOptionsListProps) => {
    const { handleDeleteModifierItemOption, isDeletingModifierItemOption } = useDeleteModifierItemOption();
    const { addModifierItemOption, isAddingModifierItemOption } = useAddModifierItemOption();
    const { updateModifierItemOption, isUpdatingModifierItemOption } = useUpdateModifierItemOption();
    const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();

    const isDisabledInputField = isDeletingModifierItemOption || isAddingModifierItemOption || isUpdatingModifierItemOption;

    const initialLocaleMaps = useMemo(() => {
      const map: Record<number, LocaleTranslationMap> = {};
      modifierItemOptions.forEach((option, index) => {
        map[index] = buildLocaleTranslationMap(option.translations, enabledLocales, {
          englishName: option.name,
          nonEnglishName: option.nonEnglishName,
        });
      });
      return map;
    }, [modifierItemOptions, enabledLocales]);

    const [localeTranslationsByIndex, setLocaleTranslationsByIndex] =
      useState<Record<number, LocaleTranslationMap>>(initialLocaleMaps);

    React.useEffect(() => {
      setLocaleTranslationsByIndex(initialLocaleMaps);
    }, [initialLocaleMaps]);

    const [editingModifierItemOptionIndex, setEditingModifierItemOptionIndex] =
      useState<number | null>(null);
    const [savingModifierItemOptionIndex, setSavingModifierItemOptionIndex] =
      useState<number | null>(null);

    const handleDeleteModifierItemOptionClick = async (
      modifierItemOption: ModifierItemOption,
      modifierItemOptionIndex: number
    ) => {
      await handleDeleteModifierItemOption(
        modifierItemOption,
        onRemoveModifierItemOptionFromUI,
        modifierItemOptionIndex
      );
    };

    const buildOptionPayload = (modifierItemOptionIndex: number, modifierItemOption: ModifierItemOption) => {
      const localeTranslations = localeTranslationsByIndex[modifierItemOptionIndex] ?? {};
      const legacy = syncLegacyNameFieldsFromLocaleTranslations(localeTranslations);
      return {
        name: legacy.name || modifierItemOption.name || "",
        nonEnglishName: legacy.nonEnglishName || undefined,
        translations: buildTranslationsPayload(localeTranslations, enabledLocales),
      };
    };

    const handleSaveModifierItemOption = async (modifierItemOptionIndex: number) => {
      const modifierItemOption = modifierItemOptions[modifierItemOptionIndex];
      const localized = buildOptionPayload(modifierItemOptionIndex, modifierItemOption);
      if (!localized.name?.trim()) return;

      setSavingModifierItemOptionIndex(modifierItemOptionIndex);
      try {
        await addModifierItemOption({
          modifierItemCorrelationId: modifierItemCorrelationId || "",
          name: localized.name,
          nonEnglishName: localized.nonEnglishName,
          translations: localized.translations,
          priceCents: convertDollarToCents(Number(modifierItemOption.priceCents) || 0),
          renderSequence: Number(modifierItemOption.renderSequence) || 0,
        });
      } catch (error) {
        console.error("Error saving new option:", error);
      } finally {
        setSavingModifierItemOptionIndex(null);
      }
    };

    const handleUpdateModifierItemOption = async (modifierItemOptionIndex: number) => {
      const modifierItemOption = modifierItemOptions[modifierItemOptionIndex];
      const localized = buildOptionPayload(modifierItemOptionIndex, modifierItemOption);

      setEditingModifierItemOptionIndex(modifierItemOptionIndex);
      try {
        await updateModifierItemOption({
          modifierItemOptionCorrelationId: modifierItemOption?.correlationId || "",
          name: localized.name,
          nonEnglishName: localized.nonEnglishName,
          translations: localized.translations,
          priceCents: convertDollarToCents(Number(modifierItemOption.priceCents) || 0),
          renderSequence: Number(modifierItemOption.renderSequence) || 0,
        });
      } catch (error) {
        console.error("Error updating option:", error);
      } finally {
        setEditingModifierItemOptionIndex(null);
      }
    };

    const handlePriceCentsBlur = (
      e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
      modifierItemOptionIndex: number,
      onUpdateModifierItemOptionFn: (
        modifierItemOptionIndex: number,
        inputFieldName: "priceCents",
        inputFieldValue: string | number
      ) => void
    ) => {
      const rawValue = e.target.value;
      if (rawValue === "") return;

      const parsed = parseFloat(rawValue);
      if (!isNaN(parsed)) {
        const formattedValue = parsed.toFixed(2);
        onUpdateModifierItemOptionFn(modifierItemOptionIndex, "priceCents", formattedValue);
      }
    };

    return (
      <div className="mb-4">
        <div className={`max-h-[400px] overflow-y-auto`}>
          <div className="text-sm mb-2">Item Options</div>

          {modifierItemOptions.map((modifierItemOption, modifierItemOptionIndex) => (
            <div key={modifierItemOption.correlationId || modifierItemOptionIndex}>
              <MenuItemLocaleFields
                enabledLocaleOptions={enabledLocaleOptions}
                defaultLocale={defaultLocale}
                localeTranslations={localeTranslationsByIndex[modifierItemOptionIndex] ?? {}}
                onChange={(next: LocaleTranslationMap) => {
                  setLocaleTranslationsByIndex((prev) => ({ ...prev, [modifierItemOptionIndex]: next }));
                  const legacy = syncLegacyNameFieldsFromLocaleTranslations(next);
                  onUpdateModifierItemOption(modifierItemOptionIndex, "name", legacy.name);
                  onUpdateModifierItemOption(modifierItemOptionIndex, "nonEnglishName", legacy.nonEnglishName);
                }}
                entityLabel="Option"
                showDescription={false}
              />
              <div className="grid grid-cols-[1.3fr_1.3fr_22px] items-center gap-[16px] py-2 mb-2">
                <FormRowVertical label="Price">
                  <div className="relative">
                    <span className="absolute text-sm left-3 top-[27px] -translate-y-1/2 text-gray-500">
                      $
                    </span>
                    <Input
                      name={`modifierItemOptions[${modifierItemOptionIndex}].priceCents`}
                      type={InputTypeEnum.NUMBER}
                      disabled={isDisabledInputField}
                      value={
                        modifierItemOption.priceCents === 0
                          ? ""
                          : modifierItemOption.priceCents
                      }
                      placeholder="0.00"
                      min="0"
                      onChange={(e) =>
                        onUpdateModifierItemOption(
                          modifierItemOptionIndex,
                          "priceCents",
                          e.target.value
                        )
                      }
                      onBlur={(e) =>
                        handlePriceCentsBlur(e, modifierItemOptionIndex, onUpdateModifierItemOption)
                      }
                      className="pl-[22px]"
                    />
                  </div>
                </FormRowVertical>

                <FormRowVertical label="Show sequence">
                  <Input
                    name={`modifierItemOptions[${modifierItemOptionIndex}].renderSequence`}
                    type={InputTypeEnum.NUMBER}
                    min="0"
                    step="1"
                    disabled={isDisabledInputField}
                    value={modifierItemOption.renderSequence}
                    onChange={(e) => {
                      const renderSequence = e.target.value;
                      if (!/^\d*$/.test(renderSequence)) return;
                      onUpdateModifierItemOption(
                        modifierItemOptionIndex,
                        "renderSequence",
                        renderSequence
                      );
                    }}
                  />
                </FormRowVertical>

                <button
                  type="button"
                  className="h-[38px] hover:bg-red-50 rounded transition-colors"
                  onClick={() =>
                    handleDeleteModifierItemOptionClick(
                      modifierItemOption as ModifierItemOption,
                      modifierItemOptionIndex
                    )
                  }
                >
                  <img
                    src={ICONS.TrashIcon}
                    className="w-full h-full mt-[12px]"
                    alt="Remove Option"
                  />
                </button>
              </div>

              <div className="flex justify-end pb-4">
                {hasModifierItemToEdit && (
                  <>
                    {modifierItemOption.correlationId ? (
                      <Button
                        size="sm"
                        isLoading={editingModifierItemOptionIndex === modifierItemOptionIndex}
                        onClick={() => handleUpdateModifierItemOption(modifierItemOptionIndex)}
                        disabled={!modifierItemOption.name?.trim()}
                      >
                        Update Option
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        isLoading={savingModifierItemOptionIndex === modifierItemOptionIndex}
                        onClick={() => handleSaveModifierItemOption(modifierItemOptionIndex)}
                        disabled={!modifierItemOption.name?.trim()}
                      >
                        Add Option
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
);

export default ModifierItemOptionsList;
